'use client';

import DOMPurify from 'dompurify';

/**
 * Sanitizes user input to prevent XSS and other injection attacks
 * @param input - The string to sanitize
 * @returns Sanitized string
 */
export const sanitizeInput = (input: string): string => {
  if (!input) return '';
  
  // First trim the input
  const trimmed = input.trim();
  
  // Use DOMPurify's sanitize method
  const sanitized = DOMPurify.sanitize(trimmed, {
    ALLOWED_TAGS: [], // No HTML tags allowed
    ALLOWED_ATTR: [], // No attributes allowed
    KEEP_CONTENT: true, // Keep content inside removed tags
    RETURN_DOM: false, // Return string, not DOM
    RETURN_DOM_FRAGMENT: false,
    RETURN_TRUSTED_TYPE: false
  });
  
  return sanitized;
};

/**
 * Validates that input doesn't contain potentially dangerous characters
 * @param input - The string to validate
 * @returns true if safe, false if potentially dangerous
 */
export const isInputSafe = (input: string): boolean => {
  if (!input) return true;
  
  // More comprehensive check for script/injection patterns
  const dangerousPatterns = [
    /javascript:/i, 
    /data:/i, 
    /vbscript:/i, 
    /on\w+=/i, // onerror, onclick, etc.
    /<script/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /\(\)/i, // function execution
    /\[\]/i, // array access
    /\{\}/i, // object literal
    /eval\(/i, // eval
    /alert\(/i, // alert
    /document\./i, // document access
    /window\./i, // window access
  ];
  
  return !dangerousPatterns.some(pattern => pattern.test(input));
};

/**
 * Sanitizes and validates a zip code
 * @param zip - The zip code to sanitize and validate
 * @returns Sanitized zip code if valid, empty string if invalid
 */
export const sanitizeZipCode = (zip: string): string => {
  if (!zip) return '';
  
  // Remove all non-digit characters
  const digitsOnly = zip.replace(/\D/g, '');
  
  // Ensure it's exactly 5 digits
  if (digitsOnly.length !== 5) {
    return '';
  }
  
  return digitsOnly;
};

/**
 * Sanitizes and validates an email address
 * @param email - The email to sanitize and validate
 * @returns Sanitized email if valid, empty string if invalid
 */
export const sanitizeEmail = (email: string): string => {
  if (!email) return '';
  
  const trimmed = email.trim().toLowerCase();
  const sanitized = DOMPurify.sanitize(trimmed);
  
  // Basic email validation
  const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!EMAIL_REGEX.test(sanitized)) {
    return '';
  }
  
  return sanitized;
};

/**
 * Sanitizes and validates a phone number
 * @param phone - The phone number to sanitize
 * @returns Sanitized phone number if valid, empty string if invalid
 */
export const sanitizePhoneNumber = (phone: string): string => {
  if (!phone) return '';
  
  // Extract only digits
  const digitsOnly = phone.replace(/\D/g, '');

  // Normalize NANP numbers: drop leading country code '1' if present
  let normalized = digitsOnly;
  if (normalized.length >= 11 && normalized.startsWith('1')) {
    normalized = normalized.slice(1);
  }

  // US only: must be exactly 10 digits after normalization
  if (normalized.length !== 10) {
    return '';
  }

  // Format as (XXX) XXX-XXXX
  return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6, 10)}`;
}; 